import { booleanAttribute, ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';

import { AdminModeService } from '../auth/admin-mode/admin-mode.service';
import { AppRouteId } from '../route-data';
import { AuthorizationWrapper } from './authorization/authorization.wrapper';
import { HeaderContainer } from './header/components/header.container';
import { BackButtonProvider, RouterBackButtonProvider } from './navigation/back-button.provider';
import { NavigationService } from './navigation/services/navigation.service';
import { trackInput } from './utils/signal/trackInput';
import { WebComponentDirective } from './web-component/web-component.directive';

/**
 * The main web component for the Customer Data Frontend application.
 *
 * This includes routing, and so is designed to be used on its own without being embedded in another application.
 */
@Component({
  selector: 'cdf-customer-data',
  imports: [HeaderContainer, AuthorizationWrapper, RouterOutlet],
  providers: [
    // These are not provided in 'root' because they depend on the router.
    NavigationService,
    { provide: BackButtonProvider, useClass: RouterBackButtonProvider },
  ],
  template: `
    @if (showHeader()) {
      <cdf-header [adminModeWarnReturnToStart]="isOnProfilePage()" (returnToStartPage)="returnToStartPage()" />
    }
    <cdf-authorization-wrapper>
      <router-outlet />
    </cdf-authorization-wrapper>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerDataApp extends WebComponentDirective {
  private readonly router = inject(Router);
  private readonly adminModeService = inject(AdminModeService);

  // NavigationService must be injected even if it is not used, so it starts tracking as soon as the app is initialised
  private readonly navigationService = inject(NavigationService);

  protected readonly isOnProfilePage = computed(
    () => this.navigationService.getCurrentPath()?.routeId === AppRouteId.PROFILE,
  );

  protected returnToStartPage() {
    if (this.navigationService.previousPathData()?.routeId === AppRouteId.CUSTOMER_SEARCH) {
      this.navigationService.back();
    } else {
      void this.navigationService.navigateToRouteId(AppRouteId.CUSTOMER_SEARCH);
    }
  }

  readonly showHeader = input(false, { transform: booleanAttribute });
  protected readonly propagateShowHeader = trackInput(this.showHeader, this.adminModeService.adminModeReachable);

  constructor() {
    super();

    this.router.initialNavigation();
  }
}
